/**
 * @file loginView.jsx
 * @author Fernando Ruano
 * @description Password Recovery View Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Input, Alert, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

// Components
import LoginTemplate from '@nephro/components/LoginTemplate';

// Helpers
import { getStorageItem } from 'commons/helpers/storage-helper';

// Styles
import styles from './styles.module.css';

function passwordRecoveryView({ authActions, loading }) {
  const { t: translate } = useTranslation();
  const [isError, setIsError] = React.useState(false);
  const [messageError, setMessageError] = React.useState(null);
  const history = useHistory();
  const [form] = Form.useForm();

  const { recoverPassword } = authActions;

  const validateMessages = {
    required: translate('commons.required'),
    types: {
      email: translate('commons.wrongEmail')
    }
  };

  const onNavigationBack = () => {
    const lastAuthPath = getStorageItem('lastAuthPath');
    history.push(lastAuthPath);
  };

  // Validate if document exists
  const onValidate = (type, response) => {
    if (type === 'success') {
      notification.success({
        message: translate('registration.notification'),
        description: translate('passwordRecovery.notification'),
        duration: 10
      });
      onNavigationBack();
    } else {
      const { code } = response;
      setMessageError(
        code !== undefined ? `errors.${code}` : 'commons.problem'
      );
      setIsError(true);
    }
  };

  // After press continue button when form full filled
  const onFinish = (values) => {
    setIsError(false);
    const { email } = values;
    const data = {
      email
    };
    recoverPassword(data, onValidate);
  };

  // After press continue button whit errors in form
  const onFinishFailed = () => setIsError(false);

  return (
    <LoginTemplate
      loading={loading}
      title="passwordRecovery.recoveryTitle"
      showContent={false}
      textContent="passwordRecovery.recoveryContent">
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onFieldsChange={onFinishFailed}
        validateMessages={validateMessages}>
        <h2 className="text-sm text-secondary mt-2 mb-7">
          {translate('passwordRecovery.recoveryContent')}
        </h2>
        <Form.Item
          label={translate('passwordRecovery.email')}
          name="email"
          className="w-full"
          rules={[
            {
              required: true,
              type: 'email'
            }
          ]}>
          <Input className="h-10" placeholder="jhon@email.com" />
        </Form.Item>

        {isError && (
          <div className="mb-4 -mt-4">
            <Alert message={translate(messageError)} type="error" showIcon />
          </div>
        )}
        <div className="flex flex-row justify-between">
          <Form.Item className="w-full">
            <Button
              onClick={onNavigationBack}
              type="link"
              className={styles.passwordRecoveryButton}>
              {translate('commons.cancel')}
            </Button>
          </Form.Item>
          <Form.Item className="w-full">
            <Button
              className={styles.passwordRecoveryButton}
              type="primary"
              htmlType="submit">
              {translate('passwordRecovery.recoveryButton')}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </LoginTemplate>
  );
}

/**
 * Component definition of proptypes.
 */
passwordRecoveryView.propTypes = {
  authActions: PropTypes.shape({
    recoverPassword: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool.isRequired
};

export default passwordRecoveryView;
